import { App, Card, ColorPicker, Form, Input, Segmented, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '@stores/auth.store';
import { updateTenantProfile } from '@services/registry';
import { useAbility } from '@/ability/AbilityProvider';
import { Button } from '@components/ui/Button';

const { Title, Text } = Typography;

const PageShell = styled.div`
    width: 100%;
    max-width: 980px;
    margin: 0 auto;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const SectionCard = styled(Card)`
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 32px rgba(15, 23, 42, 0.08);
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#ffffff')};
`;

const ControlRow = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const FieldWrap = styled.div`
    width: 100%;
    max-width: 420px;
`;

const Row2 = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    max-width: 520px;
`;

function previewLabel(fmt) {
    const d = dayjs('2026-04-28');
    return `${fmt} (${d.format(fmt)})`;
}

const DATE_FORMAT_OPTIONS = [
    'DD/MM/YYYY',
    'D/M/YYYY',
    'DD-MM-YYYY',
    'DD.MM.YYYY',
    'MM/DD/YYYY',
    'M/D/YYYY',
    'YYYY-MM-DD',
    'YYYY/MM/DD',
    'DD MMM YYYY',
    'MMM DD, YYYY',
    'MMMM D, YYYY',
    'MMMM D/YYYY',
].map((value) => ({ value, label: previewLabel(value) }));

export default function TenantSettings() {
    const { message } = App.useApp();
    const ability = useAbility();
    const user = useAuthStore((s) => s.user);
    const patchUser = useAuthStore((s) => s.patchUser);
    const tenantId = user?.tenantId;

    const canEdit = !!ability?.can?.('manage', 'Preferences');

    const initialDateFormat = user?.preferences?.dateFormat || 'DD/MM/YYYY';
    const defaultPrimary = '#F04A4A';
    const initialPrimaryColor = user?.preferences?.primaryColor || defaultPrimary;
    const defaultAvatarMode = 'theme';
    const initialAvatarMode = user?.preferences?.avatarMode || defaultAvatarMode;
    const initialAvatarColor = user?.preferences?.avatarColor || defaultPrimary;
    const initialPhoneLinkMode = user?.preferences?.phoneLinkMode || 'call';
    const initialPhoneLinkTemplate = user?.preferences?.phoneLinkTemplate || 'https://wa.me/{phone}';
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [form] = Form.useForm();
    const [section, setSection] = useState('preferences'); // future-proof switch-style nav
    const primaryColorWatch = Form.useWatch('primaryColor', form);
    const avatarModeWatch = Form.useWatch('avatarMode', form);
    const avatarColorWatch = Form.useWatch('avatarColor', form);

    const onSave = async () => {
        if (!tenantId) return;
        if (!canEdit) {
            message.error('You do not have permission to edit preferences.');
            return;
        }
        try {
            const v = await form.validateFields();
            setSaving(true);
            const nextAvatarMode = v.avatarMode || initialAvatarMode || defaultAvatarMode;
            const safeAvatarColor =
                typeof v.avatarColor === 'string' && v.avatarColor
                    ? v.avatarColor
                    : typeof user?.preferences?.avatarColor === 'string' && user.preferences.avatarColor
                        ? user.preferences.avatarColor
                        : defaultPrimary;
            const preferences = {
                ...(user?.preferences || {}),
                dateFormat: v.dateFormat,
                primaryColor: v.primaryColor,
                avatarMode: v.avatarMode,
                // Firestore does not accept `undefined`. Keep a stable color value even if mode != custom.
                avatarColor: nextAvatarMode === 'custom' ? safeAvatarColor : safeAvatarColor,
                phoneLinkMode: v.phoneLinkMode || 'call',
                phoneLinkTemplate: v.phoneLinkTemplate || 'https://wa.me/{phone}',
            };
            await updateTenantProfile(tenantId, { preferences });
            patchUser({ preferences });
            setDirty(false);
            message.success('Preferences saved');
        } catch (e) {
            if (e?.message === 'Validation failed') return;
            message.error(e?.message || 'Failed to save preferences');
        } finally {
            setSaving(false);
        }
    };

    const resetPrimary = () => {
        if (!canEdit) return;
        form.setFieldsValue({ primaryColor: defaultPrimary });
        setDirty(true);
    };

    const resetAvatar = () => {
        if (!canEdit) return;
        form.setFieldsValue({ avatarMode: defaultAvatarMode, avatarColor: defaultPrimary });
        setDirty(true);
    };

    return (
        <PageShell>
            <HeaderRow>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Settings</Title>
                    <Text type="secondary">Manage tenant preferences.</Text>
                </div>
                <Segmented
                    value={section}
                    onChange={setSection}
                    options={[
                        { value: 'preferences', label: 'Preferences' },
                    ]}
                />
            </HeaderRow>

            {section === 'preferences' ? (
                <SectionCard bordered={false} styles={{ body: { padding: 16 } }}>
                    <ControlRow>
                        <div>
                            <Title level={5} style={{ margin: 0 }}>Preferences</Title>
                            <Text type="secondary">Applies to everyone in this tenant.</Text>
                        </div>
                        <Button
                            type="primary"
                            onClick={() => void onSave()}
                            disabled={!canEdit || saving || !dirty}
                            loading={saving}
                        >
                            Save
                        </Button>
                    </ControlRow>

                    <div style={{ height: 12 }} />

                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{
                            dateFormat: initialDateFormat,
                            primaryColor: initialPrimaryColor,
                            avatarMode: initialAvatarMode,
                            avatarColor: initialAvatarColor,
                            phoneLinkMode: initialPhoneLinkMode,
                            phoneLinkTemplate: initialPhoneLinkTemplate,
                        }}
                        onValuesChange={() => setDirty(true)}
                    >
                        <Row2>
                            <FieldWrap>
                                <Form.Item
                                    name="dateFormat"
                                    label="Date format"
                                    rules={[{ required: true, message: 'Select a date format' }]}
                                >
                                    <Select
                                        options={DATE_FORMAT_OPTIONS}
                                        disabled={!canEdit}
                                        placeholder="Select date format"
                                        style={{ width: '100%' }}
                                        popupMatchSelectWidth={false}
                                    />
                                </Form.Item>
                            </FieldWrap>

                            <FieldWrap>
                                <Form.Item
                                    name="phoneLinkMode"
                                    label="Phone link behavior"
                                    rules={[{ required: true, message: 'Select how phone numbers should open' }]}
                                >
                                    <Select
                                        disabled={!canEdit}
                                        options={[
                                            { value: 'call', label: 'Open in phone call app (tel:)' },
                                            { value: 'whatsapp', label: 'Open in WhatsApp' },
                                            { value: 'custom', label: 'Custom link template' },
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, next) => prev.phoneLinkMode !== next.phoneLinkMode}
                                >
                                    {({ getFieldValue }) => {
                                        const mode = getFieldValue('phoneLinkMode');
                                        if (mode === 'whatsapp' || mode === 'custom') {
                                            return (
                                                <Form.Item
                                                    style={{ marginTop: 8 }}
                                                    name="phoneLinkTemplate"
                                                    label="Phone link template"
                                                    extra="Use {phone} placeholder. Example: https://wa.me/{phone}"
                                                    rules={[
                                                        { required: true, message: 'Enter link template' },
                                                        {
                                                            validator(_, value) {
                                                                if (!value || value.includes('{phone}')) return Promise.resolve();
                                                                return Promise.reject(new Error('Template must include {phone} placeholder'));
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        disabled={!canEdit}
                                                        placeholder="https://wa.me/{phone}"
                                                    />
                                                </Form.Item>
                                            );
                                        }
                                        return null;
                                    }}
                                </Form.Item>
                            </FieldWrap>

                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                    <Text strong>Primary color</Text>
                                    <Button $primary={false} type="button" onClick={resetPrimary} disabled={!canEdit}>
                                        Reset
                                    </Button>
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <Form.Item name="primaryColor" noStyle rules={[{ required: true, message: 'Select primary color' }]}>
                                        <ColorPicker
                                            disabled={!canEdit}
                                            showText
                                            format="hex"
                                            value={primaryColorWatch}
                                            onChange={(_, hex) => {
                                                form.setFieldsValue({ primaryColor: hex });
                                                setDirty(true);
                                            }}
                                        />
                                    </Form.Item>
                                </div>
                                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                    This updates buttons, highlights, and accents for everyone in this tenant.
                                </Text>
                            </div>

                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                    <Text strong>Avatar color</Text>
                                    <Button $primary={false} type="button" onClick={resetAvatar} disabled={!canEdit}>
                                        Reset
                                    </Button>
                                </div>
                                <div style={{ marginTop: 10 }}>
                                    <Form.Item name="avatarMode" label="Avatar style" rules={[{ required: true }]}>
                                        <Select
                                            disabled={!canEdit}
                                            options={[
                                                { value: 'theme', label: 'Use theme primary color' },
                                                { value: 'random', label: 'Random color per user' },
                                                { value: 'custom', label: 'Custom tenant color' },
                                            ]}
                                        />
                                    </Form.Item>
                                </div>

                                {avatarModeWatch === 'custom' ? (
                                    <div style={{ marginTop: 6 }}>
                                        <Form.Item name="avatarColor" noStyle rules={[{ required: true, message: 'Select avatar color' }]}>
                                            <ColorPicker
                                                disabled={!canEdit}
                                                showText
                                                format="hex"
                                                value={avatarColorWatch}
                                                onChange={(_, hex) => {
                                                    form.setFieldsValue({ avatarColor: hex });
                                                    setDirty(true);
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                ) : (
                                    <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                                        {avatarModeWatch === 'random'
                                            ? 'Each user gets a stable random color.'
                                            : 'Avatars match the theme primary color.'}
                                    </Text>
                                )}
                            </div>
                        </Row2>

                        {!canEdit ? (
                            <Text type="secondary">
                                You can view preferences but can’t edit them. Ask an admin to grant <Text strong>Preferences: Full access</Text>.
                            </Text>
                        ) : null}
                    </Form>
                </SectionCard>
            ) : null}
        </PageShell>
    );
}

